import { UtilityFunctionsService } from './../../providers/utility-functions.service';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { FirebaseAuthService } from '../../providers/firebase-auth.service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { PROFILE_REGION } from 'src/app/models/user';
import { trigger, transition, style, animate } from '@angular/animations';
import { SUBSCRIPTION_STATUS } from 'src/app/models/card';
import { MobileCommonService } from '../../mobile/mobileCommon.service';
import { UserDataService } from '../../providers/user-data.service';
import { CardsService } from '../../providers/cards.service';
import { FeedbackService } from '../../providers/feedback.service';
import { Keyboard } from '@capacitor/keyboard';
import { SettingsService } from '../../providers/settings.service';
import { UploadFileService } from '../../providers/upload-file.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-profile-main',
  templateUrl: './profile-main.component.html',
  styleUrls: ['./profile-main.component.scss'],
  animations: [
    trigger('slideToggle', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }), // Start with no height and transparent
        animate('0.2s ease-out', style({ height: '*', opacity: 1 })) // Animate to the natural height and full opacity
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, 'padding-bottom': '20px',  overflow: 'hidden' }), // Start with current height and full opacity
        animate('0.2s ease-out', style({ height: 0, opacity: 0, 'padding-bottom': '0px' })) // Animate to no height and transparent
      ])
    ])
  ]
})
export class ProfileMainComponent implements OnInit, OnDestroy {
  @Output() logoutBtnClick = new EventEmitter<boolean>();
  @Output() deleteAccBtnClick = new EventEmitter<boolean>();

  Math = Math; // Expose Math to template
  environment = environment
  PROFILE_REGION = PROFILE_REGION;
  isAccountRegionShown: boolean = false;
  isSubscriptionRegionShown: boolean = false;
  isRenderLimitRegionShown: boolean = false;
  isKeyboardShown: boolean;

  private userDataSubscription: Subscription;

  constructor(
    public firebaseAuth: FirebaseAuthService,
    public router: Router,
    public userDataService: UserDataService,
    public mobileCommonService: MobileCommonService,
    public cardService: CardsService,
    public utilityFunctionsService: UtilityFunctionsService,
    public feedbackService: FeedbackService,
    public settingService: SettingsService,
    public uploadFileService: UploadFileService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {

    // Subscribe to user data updates to refresh UI when subscription data is loaded
    this.userDataSubscription = this.firebaseAuth.userDataUpdated.subscribe(() => {
      // Force change detection to update the template
      this.cdr.detectChanges();
      console.log('User data updated in profile component - view refreshed');
    });

    if(this.settingService.devicePlatform === 'ios') {
      window.document.documentElement.style.setProperty('--base-offset', '70px');
      Keyboard.addListener('keyboardWillShow', info => {
        console.log('keyboard did show with height:', info.keyboardHeight);
        window.document.documentElement.style.setProperty('--keyboard-height', (info.keyboardHeight - 40) + 'px');
        window.document.documentElement.style.setProperty('--base-offset', '40px');
        this.isRenderLimitRegionShown = false;
        this.isAccountRegionShown = false;
        this.isSubscriptionRegionShown = false;

        this.isKeyboardShown = true;
      });

      Keyboard.addListener('keyboardWillHide', () => {
        console.log('keyboard did hide');
        window.document.documentElement.style.setProperty('--keyboard-height', '0px');
        window.document.documentElement.style.setProperty('--base-offset', '70px');

        this.isKeyboardShown = false;
      });
    }
  }

  public submitFeedback() {
    if (!this.feedbackService.feedbackText.trim() || this.feedbackService.isSubmitting) {
      return;
    }

    this.feedbackService.submitFeedback()
      .subscribe({
        next: (success) => {
          if (success) {
            this.utilityFunctionsService.showImportantSnackBar('Your feedback has been sent successfully. We will reply shortly.', 200);
            this.feedbackService.resetFeedback();
          } else {
            this.utilityFunctionsService.showImportantErrorSnackBar('Please enter your feedback before sending.', 100);
          }
        },
        error: (error) => {
          console.error('Error submitting feedback:', error);
          this.utilityFunctionsService.showImportantErrorSnackBar('Failed to send feedback. Please check your connection and try again.', 0);
        }
      });
  }

  public logoutClick() {
    this.logoutBtnClick.emit();
  }

  public deleteAccClick() {
    this.deleteAccBtnClick.emit();
  }

  public betaSettings() {
    console.log('beta settings')
    this.router.navigateByUrl('/service');
  }

  public slicedFirstName() {
    if(this.firebaseAuth?.currentUser?.displayName) {
      const str = this.firebaseAuth?.currentUser?.displayName
      return str.substring(0,str.indexOf(' ')); // "72"
    }
  }

  public isSubscriber(): boolean {
    return this.firebaseAuth.currentUser?.subscriptionStatus === SUBSCRIPTION_STATUS.SUBSCRIBER;
  }

  public isExpired(): boolean {
    return this.firebaseAuth.currentUser?.subscriptionStatus === SUBSCRIPTION_STATUS.EXPIRED;
  }

  public isTrial(): boolean {
    return this.firebaseAuth.currentUser?.subscriptionStatus === SUBSCRIPTION_STATUS.TRIAL;
  }

  public isTrialExpired(): boolean {
    return this.firebaseAuth.currentUser?.subscriptionStatus === SUBSCRIPTION_STATUS.TRIAL_EXPIRED;
  }

  public getUploadsLeft(): number {
    const uploadNumberLimit = this.firebaseAuth.currentUser?.uploadNumberLimit;
    const uploadsNumber = this.firebaseAuth.currentUser?.uploadsNumber;

    const limit = typeof uploadNumberLimit === 'number' && !isNaN(uploadNumberLimit) && uploadNumberLimit >= 0 ? uploadNumberLimit : 0;
    const usedFromBackend = typeof uploadsNumber === 'number' && !isNaN(uploadsNumber) && uploadsNumber >= 0 ? uploadsNumber : 0;

    const currentlyProcessing = this.uploadFileService.currentNumberOfUploadingOrRenderingModels;

    const actualUsed = Math.max(usedFromBackend, currentlyProcessing);
    return Math.max(0, limit - actualUsed);
  }

  public shouldShowRenderLimitSelector(): boolean {
    // Show selector only if user is subscriber and reached or is close to the limit
    if (!this.isSubscriber()) {
      return false;
    }
    const renderModelsPerCycle = this.firebaseAuth.currentUser?.renderModelsPerCycle || 0;
    const renderLimit = this.firebaseAuth.currentUser?.renderLimit || 0;
    // Show if 80% or more of the limit is used
    return renderModelsPerCycle >= renderLimit * 0.8;
  }

  public signOut() {
    this.mobileCommonService.triggerSignOut();
    console.log('signOut')
  }

  public openLegalPage(page: 'terms-of-service' | 'privacy-policy'): void {
    const isMobile = this.settingService.devicePlatform === 'ios' || this.settingService.devicePlatform === 'android';

    if (isMobile) {
      // Open external URL in system browser for mobile
      const url = `https://www.3dway.io/legal/${page}`;
      window.open(url, '_blank');
    } else {
      // Use internal routing for web
      this.router.navigate([`/legal/${page}`]);
    }
  }

  public cancelSub() {
    console.log('cancelSub clicked')
  }

  public subscribeTry() {
    console.log('subscribeTry clicked')
  }

  public onManageSubscriptionClicked() {
    this.utilityFunctionsService.showImportantErrorSnackBar(`You can't manage your membership in the app. We know, it's not ideal.`, 100);
    console.log('onManageSubscriptionClicked clicked')
  }

  public toggleRegion(event: Event, region: string) {
    console.log('region click')
    event.stopPropagation();
    switch (region) {
      case PROFILE_REGION.PERSONAL:
        this.isAccountRegionShown = !this.isAccountRegionShown;
        this.isSubscriptionRegionShown = false;
        this.isRenderLimitRegionShown = false;
        break;
      case PROFILE_REGION.SUBSCRIPT:
        this.isSubscriptionRegionShown = !this.isSubscriptionRegionShown;
        this.isAccountRegionShown = false;
        this.isRenderLimitRegionShown = false;

        break;
      case PROFILE_REGION.RENDER_LIMIT:
        this.isRenderLimitRegionShown = !this.isRenderLimitRegionShown;
        this.isAccountRegionShown = false;
        this.isSubscriptionRegionShown = false;
        break;

      default:
        break;
    }
    return
  }

  public onRenderLimitChanged(renderLimit: number): void {
    this.updateRenderLimit(renderLimit);
  }

  public getFormattedEndDate(): string {
    const subscriptionEndDate = new Date(this.firebaseAuth.currentUser.subscriptionEndDateTime);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return subscriptionEndDate.toLocaleDateString('en-US', options);
  }

  public getSubscriptionRenewalDate(): string {
    if (!this.firebaseAuth.currentUser || !this.firebaseAuth.currentUser.subscriptionEndDateTime) {
      return null;
    }

    const subscriptionEndDate = new Date(this.firebaseAuth.currentUser.subscriptionEndDateTime);
    subscriptionEndDate.setDate(subscriptionEndDate.getDate() + 1);

    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return subscriptionEndDate.toLocaleDateString('en-US', options);
  }

  public isSubscriptionEndLabelShown(): boolean {
    return !this.firebaseAuth.isSubscriptionRelatedBtnDisabled &&
            this.firebaseAuth.currentUser?.subscriptionStatus &&
            this.firebaseAuth.currentUser?.subscriptionEndDateTime &&
           !this.firebaseAuth.currentUser?.isAutoRenewDisabled &&
            this.firebaseAuth.currentUser?.subscriptionStatus === SUBSCRIPTION_STATUS.SUBSCRIBER;
  }

  private updateRenderLimit(renderLimit: number): void {
    this.firebaseAuth.currentUser.renderLimit = renderLimit;
    this.cardService.setRenderLimit(this.firebaseAuth.currentUser.uid, this.firebaseAuth.currentUser.renderLimit)
      .then((resp) => {
        console.log('Billing protection value updated', resp);
      })
      .catch((err) => {
        this.utilityFunctionsService.showImportantErrorSnackBar('Something went wrong while updating billing protection value', 0);
        console.error(err);
      });
  }

  ngOnDestroy(): void {
    window.document.documentElement.style.setProperty('--keyboard-height', '0px');
    window.document.documentElement.style.setProperty('--base-offset', '70px');

    if(this.settingService.devicePlatform === 'ios') { Keyboard.removeAllListeners(); }

    // Unsubscribe to prevent memory leaks
    if (this.userDataSubscription) {
      this.userDataSubscription.unsubscribe();
    }
  }


}
